// app/(routes)/news/components/NewsletterMarketingComponent.tsx
"use client";

import { ArrowRight, Smartphone, Zap } from "lucide-react";
import Image from "next/image";
import Link from "next/link";

import { PublicNewsEdition } from "@/app/features/news/news.interface";
import { HTFButton } from "@/app/shared/components/button/HTFButtonComponent";
import { PersonalizationProfileDisplayComponent } from "@/app/shared/components/display/PersonalizationProfileDisplayComponent";
import { HTFHeading } from "@/app/shared/components/typography/HTFHeading";

interface NewsletterMarketingSectionProps {
  editions: PublicNewsEdition[];
  isLoading: boolean;
}

export function NewsletterMarketingSection({
  editions,
  isLoading,
}: NewsletterMarketingSectionProps) {
  return (
    <>
      {/* Hero Section - Left Aligned */}
      <section className="bg-surface-light">
        <div className="container mx-auto px-4 py-16">
          <div className="max-w-6xl mx-auto">
            <div className="space-y-8">
              <div className="space-y-6">
                <div className="inline-flex items-center gap-2 bg-primary/10 text-primary px-4 py-2 rounded-full text-sm font-semibold">
                  <Zap size={16} />
                  New Product Launch
                </div>

                <HTFHeading
                  as="h1"
                  size="5xl"
                  fontWeight="extrabold"
                  className="text-black leading-tight max-w-4xl"
                >
                  AI news that's specially crafted for you.
                  <br />
                  <span className="text-primary">
                    In the language you understand.
                  </span>
                </HTFHeading>

                <p className="text-xl text-text-secondary leading-relaxed max-w-3xl">
                  Most AI newsletters are generic - either too technical or too
                  shallow. We take a different approach: tailored content for
                  your technical level, role, and business context.
                </p>
              </div>

              <div className="flex flex-col sm:flex-row gap-4">
                <HTFButton
                  asChild
                  variant="primary"
                  size="lg"
                  iconAfter={<ArrowRight size={20} />}
                  className="shadow-lg text-black"
                >
                  <Link href="/contact?source=news_hero">
                    Discuss AI briefings for your team
                  </Link>
                </HTFButton>
                <p className="text-sm text-text-secondary self-center">
                  Configured for your organization
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Our Approach - Personalization Process */}
      <section className="py-16 bg-gradient-to-b from-beige via-surface-light to-beige">
        <div className="container mx-auto px-4">
          <div className="max-w-7xl mx-auto">
            <div className="space-y-16">
              <div className="space-y-6">
                <HTFHeading
                  as="h2"
                  size="4xl"
                  fontWeight="bold"
                  className="text-black"
                >
                  Tailored AI News for You
                </HTFHeading>
                <p className="text-xl text-text-secondary leading-relaxed max-w-3xl">
                  We understand your current AI and technical skills, as well as
                  your business stage and craft a personalized newsletter on how
                  you can use AI.
                </p>
              </div>

              {/* Use the exact same component from homepage */}
              <PersonalizationProfileDisplayComponent />
            </div>
          </div>
        </div>
      </section>

      {/* Editorial Process Section */}
      <section className="py-16 bg-beige">
        <div className="container mx-auto px-4">
          <div className="max-w-7xl mx-auto">
            <div className="space-y-16">
              <div className="space-y-6">
                <HTFHeading
                  as="h2"
                  size="4xl"
                  fontWeight="bold"
                  className="text-black"
                >
                  From raw news to business insights
                </HTFHeading>
                <p className="text-xl text-text-secondary leading-relaxed max-w-4xl">
                  Our editorial team handpicks the most important AI
                  developments, then our AI system crafts a personalized version
                  that shows exactly how each story impacts your specific
                  business.
                </p>
              </div>

              {/* Three Column Process - Similar to homepage design */}
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 h-auto lg:h-[500px]">
                {/* Left Column: Editorial Curation */}
                <div className="rounded-lg p-6 h-full flex flex-col">
                  <h4 className="text-lg font-semibold text-gray-800 mb-6 text-center">
                    Editorial Curation
                  </h4>
                  <div className="space-y-4 flex-1">
                    <div className="bg-white rounded-lg p-4 shadow-sm border-l-4 border-blue-500">
                      <h5 className="font-semibold text-gray-900 text-sm mb-2">
                        Claude 4.1 Opus Launch
                      </h5>
                      <p className="text-xs text-gray-600">
                        Revolutionary reasoning capabilities in latest Anthropic
                        model...
                      </p>
                    </div>
                    <div className="bg-white rounded-lg p-4 shadow-sm border-l-4 border-purple-500">
                      <h5 className="font-semibold text-gray-900 text-sm mb-2">
                        Google Gemini 2.5 for Medical Imaging
                      </h5>
                      <p className="text-xs text-gray-600">
                        New AI can analyze X-rays and MRIs with 94% accuracy...
                      </p>
                    </div>
                    <div className="bg-white rounded-lg p-4 shadow-sm border-l-4 border-green-500">
                      <h5 className="font-semibold text-gray-900 text-sm mb-2">
                        Healthcare AI ROI Study
                      </h5>
                      <p className="text-xs text-gray-600">
                        500+ hospitals report $180K average annual savings...
                      </p>
                    </div>
                    <div className="text-center pt-4">
                      <span className="text-xs text-gray-500 bg-gray-100 px-3 py-1 rounded-full">
                        + 15 more stories curated this week
                      </span>
                    </div>
                  </div>
                </div>

                {/* Middle Column: AI Processing */}
                <div className="bg-beige-dark rounded-lg p-6 h-full flex flex-col">
                  <h4 className="text-lg font-semibold text-gray-800 mb-6 text-center">
                    AI Personalization Engine
                  </h4>

                  <div className="flex-1 space-y-6">
                    {/* LLM Processing Visual */}
                    <div className="bg-gradient-to-r from-primary/10 to-secondary/10 rounded-lg p-4">
                      <div className="flex items-center gap-2 mb-3">
                        <div className="w-6 h-6 bg-primary rounded-full flex items-center justify-center">
                          <span className="text-xs text-white font-bold">
                            AI
                          </span>
                        </div>
                        <span className="text-sm font-semibold text-gray-800">
                          LLM Analysis
                        </span>
                      </div>
                      <div className="space-y-2 text-xs text-gray-700">
                        <p>
                          • Analyzing story relevance for Healthcare Operations
                        </p>
                        <p>• Translating tech jargon into simple terms</p>
                        <p>
                          • Calculating real-world time savings for hospitals
                        </p>
                        <p>• Explaining HIPAA compliance requirements</p>
                      </div>
                    </div>

                    {/* User Context Integration */}
                    <div className="bg-white/50 rounded-lg p-4">
                      <div className="flex items-center gap-2 mb-3">
                        <div className="w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center">
                          <span className="text-xs text-white">👤</span>
                        </div>
                        <span className="text-sm font-semibold text-gray-800">
                          Context Integration
                        </span>
                      </div>
                      <div className="text-xs text-gray-700">
                        <p>
                          Combining user's beginner tech level with healthcare
                          context to create simple, actionable insights
                        </p>
                      </div>
                    </div>

                    {/* Processing Arrow */}
                    <div className="flex justify-center">
                      <div className="flex items-center gap-2 text-primary">
                        <span className="text-sm font-medium">Processing</span>
                        <div className="flex gap-1">
                          <div className="w-2 h-2 bg-primary rounded-full animate-pulse" />
                          <div
                            className="w-2 h-2 bg-primary rounded-full animate-pulse"
                            style={{ animationDelay: "0.2s" }}
                          />
                          <div
                            className="w-2 h-2 bg-primary rounded-full animate-pulse"
                            style={{ animationDelay: "0.4s" }}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Right Column: Business Impact Output */}
                <div className="rounded-lg p-6 h-full flex flex-col">
                  <h4 className="text-lg font-semibold text-gray-800 mb-6 text-center">
                    Business Impact Insights
                  </h4>
                  <div className="space-y-4 flex-1">
                    {/* Claude 4.1 Card - Green accent */}
                    <div className="bg-surface rounded-lg shadow-md overflow-hidden h-[120px] flex flex-col relative">
                      <div className="h-1.5 bg-green-500" />
                      <div className="absolute top-3 right-3 z-10">
                        <span className="text-xs text-white font-medium px-2 py-1 bg-green-500 rounded">
                          High Impact
                        </span>
                      </div>
                      <div className="p-4 flex-1 flex flex-col">
                        <div className="flex items-start gap-3 mb-2">
                          <span className="text-lg">🏥</span>
                          <div className="flex-1 min-w-0">
                            <h5 className="text-sm font-semibold text-gray-900 leading-tight">
                              Claude 4.1 for Patient Records
                            </h5>
                            <p className="text-xs text-gray-600">
                              "Reduce paperwork by 60% in 3 weeks"
                            </p>
                          </div>
                        </div>
                        <div className="mt-auto bg-beige-light rounded p-2">
                          <p className="text-xs text-gray-800">
                            <strong>Setup:</strong> 2-3 weeks •{" "}
                            <strong>Savings:</strong> 2 hrs/nurse/shift
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Gemini 2.0 Card - Indigo accent */}
                    <div className="bg-surface rounded-lg shadow-md overflow-hidden h-[120px] flex flex-col relative">
                      <div className="h-1.5 bg-indigo" />
                      <div className="absolute top-3 right-3 z-10">
                        <span className="text-xs text-white font-medium px-2 py-1 bg-indigo rounded">
                          Future-Ready
                        </span>
                      </div>
                      <div className="p-4 flex-1 flex flex-col">
                        <div className="flex items-start gap-3 mb-2">
                          <span className="text-lg">📋</span>
                          <div className="flex-1 min-w-0">
                            <h5 className="text-sm font-semibold text-gray-900 leading-tight">
                              Gemini 2.5 Radiology AI
                            </h5>
                            <p className="text-xs text-gray-600">
                              "94% accurate X-ray analysis, FDA approved"
                            </p>
                          </div>
                        </div>
                        <div className="mt-auto bg-beige-light rounded p-2">
                          <p className="text-xs text-gray-800">
                            <strong>Available:</strong> Q1 2025 •{" "}
                            <strong>Impact:</strong> 3x faster diagnosis
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* ROI Card - Orange accent */}
                    <div className="bg-surface rounded-lg shadow-md overflow-hidden h-[120px] flex flex-col relative">
                      <div className="h-1.5 bg-orange-500" />
                      <div className="absolute top-3 right-3 z-10">
                        <span className="text-xs text-white font-medium px-2 py-1 bg-orange-500 rounded">
                          Cost Savings
                        </span>
                      </div>
                      <div className="p-4 flex-1 flex flex-col">
                        <div className="flex items-start gap-3 mb-2">
                          <span className="text-lg">💰</span>
                          <div className="flex-1 min-w-0">
                            <h5 className="text-sm font-semibold text-gray-900 leading-tight">
                              Healthcare AI ROI
                            </h5>
                            <p className="text-xs text-gray-600">
                              "Real savings from 50+ hospitals"
                            </p>
                          </div>
                        </div>
                        <div className="mt-auto bg-beige-light rounded p-2">
                          <p className="text-xs text-gray-800">
                            <strong>Average:</strong> $180K saved per year
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Newsletter Result Section */}
      <section className="py-16 bg-surface-light">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="space-y-16">
              <div className="space-y-6">
                <HTFHeading
                  as="h2"
                  size="4xl"
                  fontWeight="bold"
                  className="text-black"
                >
                  Your Personalized Newsletter Edition
                </HTFHeading>
                <p className="text-xl text-text-secondary leading-relaxed max-w-4xl">
                  We craft stories specifically relevant to your role,
                  experience level, and industry context. Here's an example:
                  Sarah works at a regional hospital and has beginner-level tech
                  experience. Watch how the same AI stories become completely
                  different when tailored for her.
                </p>
              </div>

              {/* Sarah's Profile - Compact Single Row */}
              <div className="bg-beige-dark rounded-xl p-6 max-w-4xl mx-auto">
                <div className="space-y-4">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center text-white font-bold">
                      S
                    </div>
                    <div>
                      <h4 className="text-lg font-semibold text-gray-900">
                        Sarah's Profile (Example User)
                      </h4>
                      <p className="text-sm text-gray-600">
                        Healthcare Operations Manager
                      </p>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 md:grid-cols-6 gap-4 text-sm">
                    <div className="space-y-1">
                      <p className="text-gray-600 font-medium">Experience</p>
                      <p className="text-gray-900">Beginner</p>
                    </div>
                    <div className="space-y-1">
                      <p className="text-gray-600 font-medium">Tech Level</p>
                      <p className="text-gray-900">Beginner</p>
                    </div>
                    <div className="space-y-1">
                      <p className="text-gray-600 font-medium">AI Experience</p>
                      <p className="text-gray-900">Beginner</p>
                    </div>
                    <div className="space-y-1">
                      <p className="text-gray-600 font-medium">Organization</p>
                      <p className="text-gray-900">Regional Hospital</p>
                    </div>
                    <div className="space-y-1">
                      <p className="text-gray-600 font-medium">Team Size</p>
                      <p className="text-gray-900">500+ employees</p>
                    </div>
                    <div className="space-y-1">
                      <p className="text-gray-600 font-medium">Industry</p>
                      <p className="text-gray-900">Healthcare</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="text-center">
                <div className="inline-flex items-center gap-2 text-primary">
                  <span className="text-lg">⬇️</span>
                  <span className="font-medium">
                    Here's how her newsletter looks:
                  </span>
                </div>
              </div>

              {/* Newsletter Preview - Featured Card Style with Image */}
              <div className="bg-layer-1 rounded-2xl overflow-hidden shadow-xl border-0">
                <div className="lg:flex">
                  {/* Image Section */}
                  <div className="lg:w-1/2">
                    <div
                      className="relative h-80 lg:h-full flex items-center justify-center overflow-hidden"
                      style={{ backgroundColor: "#D97757" }}
                    >
                      {/* Anthropic Claude 4.1 Opus Image */}
                      <Image
                        src="https://www-cdn.anthropic.com/images/4zrzovbb/website/a97733b3607b54a30778eb89de08afd9e02b9fb3-1000x1000.svg"
                        alt="Claude 4.1 Opus announcement"
                        className="w-full h-full object-contain p-8"
                        width={1000}
                        height={1000}
                      />
                      {/* Subtle overlay for text readability */}
                      <div className="absolute inset-0 bg-gradient-to-t from-black/20 via-transparent to-transparent" />
                      {/* Newsletter badge overlay */}
                      <div className="absolute bottom-6 left-6 right-6">
                        <div className="bg-white/95 backdrop-blur-sm rounded-lg p-4 shadow-lg">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center text-white font-bold text-sm">
                              🤖
                            </div>
                            <div>
                              <h4 className="font-semibold text-gray-900">
                                AI Weekly
                              </h4>
                              <p className="text-sm text-gray-600">
                                Personalized Edition
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Content Section */}
                  <div className="lg:w-1/2 p-8 lg:p-12 flex flex-col justify-center">
                    <div className="space-y-6">
                      <div className="flex items-center gap-3">
                        <div className="inline-flex items-center gap-2 px-3 py-1 bg-primary/10 text-primary rounded-full text-sm font-semibold">
                          <span className="w-6 h-6 bg-primary rounded-full flex items-center justify-center text-white text-xs">
                            ✨
                          </span>
                          Personalized
                        </div>
                        <span className="text-gray-500 text-sm">
                          Wednesday, Sep 11, 2025
                        </span>
                      </div>

                      <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 leading-tight">
                        This week: Claude 4.1 for Healthcare
                      </h2>

                      <p className="text-gray-600 text-lg leading-relaxed">
                        Sarah, your hospital spends $2.3M annually on
                        administrative tasks. Claude 4.1 can cut that to $920K
                        by automatically processing discharge summaries,
                        insurance forms, and medication reconciliation -
                        starting next month.
                      </p>

                      <div className="flex items-center gap-4 text-sm text-gray-500">
                        <span>6 stories</span>
                        <div className="w-1 h-1 bg-gray-300 rounded-full" />
                        <span>4-6 min read</span>
                        <div className="w-1 h-1 bg-gray-300 rounded-full" />
                        <span className="text-primary font-medium">
                          Tailored for Healthcare Operations
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Newsletter Content Preview */}
              <div className="bg-white rounded-2xl overflow-hidden shadow-lg max-w-4xl mx-auto">
                <div className="p-8">
                  {/* Header - simplified */}
                  <div className="text-center space-y-4 mb-8 pb-8 border-b border-gray-200">
                    <div className="inline-flex items-center gap-2 px-4 py-2 bg-blue-50 rounded-full">
                      <span className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center text-white font-bold text-sm">
                        🤖
                      </span>
                      <span className="text-blue-800 font-semibold text-sm">
                        Your AI News Brief
                      </span>
                    </div>
                  </div>

                  {/* Newsletter Content */}
                  <div className="space-y-8">
                    {/* Featured Story */}
                    <article className="space-y-6">
                      <div className="space-y-4">
                        <div className="flex items-center gap-2">
                          <span className="text-2xl">💡</span>
                          <span className="text-xs text-blue-600 bg-blue-50 px-2 py-1 rounded-full font-medium">
                            FEATURED STORY
                          </span>
                        </div>

                        <h2 className="text-2xl font-bold text-gray-900 leading-tight">
                          Claude 4.1 Eliminates 14 Hours of Weekly Paperwork Per
                          Nurse
                        </h2>

                        <p className="text-gray-700 leading-relaxed">
                          Regional hospitals like yours are already seeing
                          results. At Mercy General (similar size to your
                          facility), nurses now spend 14 fewer hours per week on
                          documentation. The AI reads patient charts,
                          automatically fills insurance forms, and flags
                          critical medication interactions before doctors even
                          see the patient.
                        </p>

                        {/* Story metadata */}
                        <div className="flex items-center justify-between text-sm text-gray-500">
                          <div className="flex items-center gap-3">
                            <span className="flex items-center gap-1">
                              <span>💰</span>
                              Implementation: ~$800/month
                            </span>
                            <span className="flex items-center gap-1">
                              <span>⏱️</span>
                              3-6 weeks to ship
                            </span>
                          </div>
                          <span className="text-green-600 font-medium text-xs">
                            ✨ Personalized for You
                          </span>
                        </div>
                      </div>
                    </article>

                    {/* Additional Stories */}
                    <section className="space-y-6">
                      <h3 className="text-lg font-semibold text-gray-900 border-b border-gray-200 pb-2">
                        More Stories This Week
                      </h3>

                      <div className="space-y-4">
                        <article className="flex gap-4 p-4 bg-gray-50 rounded-lg">
                          <span className="text-xl flex-shrink-0">🩻</span>
                          <div className="space-y-2">
                            <h4 className="font-semibold text-gray-900">
                              Gemini 2.5 Reads X-Rays Better Than Radiologists
                            </h4>
                            <p className="text-sm text-gray-600">
                              FDA approves Google's AI for emergency room
                              diagnosis - cuts wait times by 60%
                            </p>
                            <div className="text-xs text-gray-500">
                              3 min read • High impact
                            </div>
                          </div>
                        </article>

                        <article className="flex gap-4 p-4 bg-gray-50 rounded-lg">
                          <span className="text-xl flex-shrink-0">💊</span>
                          <div className="space-y-2">
                            <h4 className="font-semibold text-gray-900">
                              AI Prevents 89% of Medication Errors
                            </h4>
                            <p className="text-sm text-gray-600">
                              Mayo Clinic study: AI drug interaction checker
                              saves 2,400 lives annually
                            </p>
                            <div className="text-xs text-gray-500">
                              4 min read • Critical safety
                            </div>
                          </div>
                        </article>

                        <article className="flex gap-4 p-4 bg-gray-50 rounded-lg">
                          <span className="text-xl flex-shrink-0">🚨</span>
                          <div className="space-y-2">
                            <h4 className="font-semibold text-gray-900">
                              AI Sepsis Alert System Goes Live
                            </h4>
                            <p className="text-sm text-gray-600">
                              Johns Hopkins deploys early warning AI - reduces
                              sepsis deaths by 18%
                            </p>
                            <div className="text-xs text-gray-500">
                              2 min read • Life-saving tech
                            </div>
                          </div>
                        </article>
                      </div>
                    </section>
                  </div>

                  {/* Newsletter Footer */}
                  <footer className="px-8 py-8 border-t border-gray-200 bg-gray-50">
                    <div className="text-center space-y-4">
                      <p className="text-gray-600 leading-relaxed">
                        Thanks for reading today's AI newsletter. Stay ahead of
                        the curve with the latest in artificial intelligence.
                      </p>
                      <div className="text-sm text-gray-500 space-y-1">
                        <p>© 2024 HowTheF AI</p>
                        <p>4 stories curated for you</p>
                      </div>
                    </div>
                  </footer>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* WhatsApp Delivery Section */}
      <section className="py-16 bg-beige">
        <div className="container mx-auto px-4">
          <div className="max-w-7xl mx-auto">
            <div className="space-y-16">
              <div className="space-y-6 text-center">
                <HTFHeading
                  as="h2"
                  size="4xl"
                  fontWeight="bold"
                  className="text-black"
                >
                  Delivered to your WhatsApp
                </HTFHeading>
                <p className="text-xl text-text-secondary leading-relaxed max-w-4xl mx-auto">
                  Every Tuesday morning, you get your personalized edition
                  delivered directly to WhatsApp. No email clutter, no missed
                  notifications.
                </p>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                <div className="space-y-6">
                  <p className="text-lg text-text-secondary leading-relaxed">
                    Get a concise summary with the key stories that matter to
                    your specific situation, delivered right to your phone.
                  </p>
                  <div className="space-y-4">
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0">
                        <span className="text-white text-sm">📱</span>
                      </div>
                      <div>
                        <h4 className="font-semibold text-black mb-1">
                          Direct to your phone
                        </h4>
                        <p className="text-text-secondary text-sm">
                          No need to check email or remember to visit a website
                        </p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center flex-shrink-0">
                        <span className="text-white text-sm">⚡</span>
                      </div>
                      <div>
                        <h4 className="font-semibold text-black mb-1">
                          3-minute read
                        </h4>
                        <p className="text-text-secondary text-sm">
                          Perfect for your morning coffee or commute
                        </p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 bg-orange-500 rounded-full flex items-center justify-center flex-shrink-0">
                        <span className="text-white text-sm">🎯</span>
                      </div>
                      <div>
                        <h4 className="font-semibold text-black mb-1">
                          Actionable insights
                        </h4>
                        <p className="text-text-secondary text-sm">
                          Every story includes specific costs, timelines, and
                          next steps
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="flex justify-center">
                  {/* WhatsApp Template - Matching your actual template */}
                  <div className="bg-surface rounded-2xl p-6 shadow-xl max-w-sm w-full">
                    {/* WhatsApp Header */}
                    <div className="flex items-center gap-3 mb-6 pb-4 border-b border-gray-200">
                      <div className="w-10 h-10 bg-green-500 rounded-full flex items-center justify-center">
                        <Smartphone size={20} className="text-white" />
                      </div>
                      <div>
                        <h4 className="font-semibold text-black">
                          howtheF AI news
                        </h4>
                        <p className="text-xs text-gray-500">11/09/2025</p>
                      </div>
                    </div>

                    {/* WhatsApp Message */}
                    <div className="space-y-4">
                      <p className="text-black font-medium">
                        Good morning, Sarah!
                      </p>

                      <div className="space-y-3">
                        <p className="text-black font-medium">
                          In today's edition
                        </p>

                        <div className="bg-beige-light rounded-lg p-4 space-y-3">
                          <div className="flex items-start gap-2">
                            <span className="text-red-500 mt-1">🏥</span>
                            <div>
                              <p className="text-black text-sm font-medium">
                                Main Story: Claude 4.1 eliminates 14 hours of
                                weekly paperwork per nurse. Mercy General (500
                                beds, like yours) already seeing results.
                              </p>
                            </div>
                          </div>
                        </div>

                        <div className="space-y-2">
                          <p className="text-black font-medium">
                            For your 500-bed hospital
                          </p>
                          <div className="flex items-start gap-2">
                            <span className="text-yellow-500 mt-1">💰</span>
                            <p className="text-black text-sm">
                              Annual admin costs: $2.3M → $920K (60% reduction).
                              Implementation: 3 weeks. Your nurses get 14 hours
                              back per week for patient care...
                            </p>
                          </div>
                        </div>

                        <div className="text-xs text-gray-500 space-y-1">
                          <p>
                            Don't want to receive these anymore? reply
                            UNSUBSCRIBE
                          </p>
                          <p className="text-right">21:36</p>
                        </div>

                        <div className="pt-3 border-t border-gray-200">
                          <div className="flex items-center justify-center">
                            <div className="bg-primary/10 text-primary px-4 py-2 rounded-lg text-sm font-medium">
                              🔗 Visit website
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-16 bg-secondary">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center space-y-6">
              <HTFHeading
                as="h2"
                size="4xl"
                fontWeight="bold"
                className="text-black max-w-3xl mx-auto"
              >
                Ready for AI news that actually makes sense for your work?
              </HTFHeading>
              <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                <HTFButton
                  asChild
                  variant="primary"
                  size="lg"
                  iconAfter={<ArrowRight size={20} />}
                  className="shadow-lg text-black"
                >
                  <Link href="/contact?source=news_final_cta">
                    Talk About a Team Edition
                  </Link>
                </HTFButton>
                <p className="text-sm text-text-secondary">
                  Built around your team, role, and business context
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
